import axios, { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilCallback, useSetRecoilState } from "recoil";

import {
  apiBackgroundLoadingErrorsAtom,
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  ApiException,
  errors,
  IApiError,
  IFetchOptions,
  IRideBookingInfoDto,
  Log,
  normalizeErrors,
  rideBookingsAF,
  RidesApi,
  toForbiddenTranslatableItem,
  translateInfoItem,
} from "@ryde/shared";

export interface IFetchRideBookingsOptions extends IFetchOptions {
  rideId: number;
}

export const useRideBookingActions = () => {
  const { t } = useTranslation();
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setBackgroundLoadingErrors = useSetRecoilState(
    apiBackgroundLoadingErrorsAtom,
  );
  const setLoading = useSetRecoilState(apiDataFetchingAtom);
  const setRideBookingsAtomData = useEvent(
    useRecoilCallback(
      ({ set }) =>
        (rideId: number, value: IRideBookingInfoDto[]) => {
          set(rideBookingsAF(rideId), value);
        },
    ),
  );

  const fetchRideBookings = useEvent(
    async (rideId: number, fetchOptions?: IFetchOptions): Promise<boolean> => {
      const { abortController, backgroundAction } = fetchOptions ?? {};

      try {
        if (backgroundAction) {
          setBackgroundLoadingErrors(undefined);
        } else {
          setErrors(undefined);
          setLoading(true);
        }

        Log.debug(`Fetching booking dtos for ride ${rideId}`);
        const apiResult = await RidesApi.searchRideBookings(
          rideId,
          abortController,
        );

        if (!apiResult) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return false;
        }

        Log.debug(`Booking dto received for rideId ${rideId}`, apiResult);
        const bookingsData = apiResult?.data || [];
        setRideBookingsAtomData(rideId, bookingsData);

        return true;
      } catch (error) {
        if (axios.isCancel(error)) {
          Log.debug("Axios, request canceled", error);
          return false;
        }
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          Log.errorAndToast(
            t(errors.ride.failedFetchingBookingDTOs),
            errorDetails,
          );
          return false;
        }

        Log.errorAndToast(t(errors.ride.failedFetchingBookingDTOs), error);

        if (backgroundAction) {
          setErrors(normalizeErrors(error as IApiError, t));
        } else {
          setBackgroundLoadingErrors(normalizeErrors(error as IApiError, t));
        }

        return false;
      } finally {
        if (!backgroundAction) {
          setLoading(false);
        }
      }
    },
  );

  const fetchBookingsForEditingRide = useEvent((rideId: number | undefined) => {
    if (!rideId) {
      return;
    }

    fetchRideBookings(rideId, { backgroundAction: true });
  });

  return { fetchRideBookings, fetchBookingsForEditingRide };
};
